import api from "@/composables/api";
import { ref } from "vue";
import { useUser } from "@/stores";

const EMAIL_REGEX = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/;
const BASE_URL = import.meta.env.VITE_APP_BASE_URL;
const GENERIC_ERROR = "Something went wrong. Contact support.";

export default () => {
  const userStore = useUser();
  const reviewers = ref([]);
  const loading = ref(false);
  const imageDataURIs = {};
  const totalReviewerCount = ref(0);
  const pinElementsDict = ref({});

  const updateReviewers = (newReviewers) => {
    // console.log("updateReviewers newReviewers", newReviewers);
    const processedReviewers = newReviewers.map((reviewer) => ({
      ...reviewer,
      emailHash: `${BASE_URL}r/${reviewer.emailHash}`,
    }));
    // .filter((reviewer) => reviewer.status !== "archived");
    // .reverse();

    reviewers.value = processedReviewers;
  };

  /**
   * Retrieves the specific page of reviewers for the Reviewers tab
   * @param {number} pageNumber - The number of the page to retrieve
   * @param {number} pageSize - The number of reviewers per page
   * @param {boolean} [force=false] - Whether to force a refresh of the data
   * @returns updates the reviewers value with the new data
   */
  const fetchReviewers = async (pageNumber = 0, pageSize, force = false) => {
    // console.log("reviewers", reviewers.value);
    loading.value = true;
    if (reviewers.value.length > 0 && !force) {
      loading.value = false;
      return;
    }

    if (pageNumber.value < 1) {
      pageNumber.value = 1;
      return;
    }

    try {
      let offset = (pageNumber - 1) * pageSize;
      if (offset < 0) {
        offset = 0;
      }
      const url = `v1/reviewer/?offset=${offset}&pageSize=${pageSize}`;
      const res = await api(url);
      if (!res) {
        console.error("Error fetching reviewers:", res);
        return;
      }
      totalReviewerCount.value = res.data.totalReviewerCount;
      updateReviewers(res.data.reviewers);
    } catch (error) {
      console.error("Error fetching reviewers:", error);
    } finally {
      // Delay loading false by 1 second to prevent flickering
      setTimeout(() => {
        loading.value = false;
      }, 1000);
    }
  };

  fetchReviewers(0, 10);

  // const fetchReviewers = async (limit, force = false) => {
  //   loading.value = true;
  //   if (reviewers.value.length > 0 && !force) {
  //     loading.value = false;
  //     return;
  //   }
  //   try {
  //     // Construct the URL with query parameter for limit if provided
  //     const url = limit ? `v1/reviewer/?limit=${limit}` : "v1/reviewer/";
  //     const res = await api(url);
  //     totalReviewerCount.value = res.data.totalReviewerCount;
  //     updateReviewers(res.data.reviewers);
  //   } catch (error) {
  //     console.error("Error fetching reviewers:", error);
  //   } finally {
  //     //delay loading false by 1 second to prevent flickering
  //     setTimeout(() => {
  //       loading.value = false;
  //     }, 1000);
  //   }
  // };

  // const fetchReviewers = async () => {
  //   loading.value = true;
  //   try {
  //     const res = await api(`v1/reviewer/`);
  //     updateReviewers(res.data.reviewers);
  //   } catch (error) {
  //     console.error("Error fetching reviewers:", error);
  //   }

  //   //delay loading false by 1 second to prevent flickering
  //   setTimeout(() => {
  //     loading.value = false;
  //   }, 1000);
  // };

  const isValidEmail = (email) => EMAIL_REGEX.test(email);

  const areSettingsIncomplete = () => {
    if (userStore.settings.companyName === "") {
      return "Please set your <b>Company Name</b> in <b>Settings</b> before creating a reviewer.";
    }

    if (userStore.settings.feedbackQuestion.length > 0) {
      if (userStore.settings.companyReviewLink === "") {
        return "Please set your <b>Positive Feedback Link</b> or clear the <b>Feedback Question</b> in <b>Settings</b> before creating a reviewer.";
      }
    }
    return false;
  };

  const createReviewer = async (rawEmail, rawPin, optionalPayment) => {
    if (!isValidEmail(rawEmail)) {
      return { error: true, message: "Please enter a valid email address" };
    }

    const settingsIncomplete = areSettingsIncomplete();
    if (settingsIncomplete) {
      return {
        error: true,
        message: settingsIncomplete,
      };
    }

    try {
      const res = await api(`v1/reviewer/`, {
        method: "POST",
        body: { email: rawEmail, pin: rawPin, optionalPayment },
      });
      if (res.data && res.data.reviewers) {
        await updateReviewers(res.data.reviewers);
        return { error: false, message: "Reviewer created successfully" };
      }
      return { error: true, message: GENERIC_ERROR };
    } catch (err) {
      console.error("Error creating reviewer:", err);
      return { error: true, message: GENERIC_ERROR };
    }
  };

  const getReviewerPins = async () => {
    try {
      const res = await api(`v1/reviewer/pins`);
      // console.log("res.data getReviewPins", res.data);
      pinElementsDict.value = res.data;
      for (const el of res.data) {
        //append entire element to dictionary
        pinElementsDict.value[el._id] = el.pin;
      }
      // return saveElementsAndReturnPins(res.data);
      // console.log("pinElementsDict.value", pinElementsDict.value);
      return pinElementsDict.value;
    } catch (error) {
      console.error("Error fetching reviewer pins:", error);
      return [];
    }
  };

  const getReviewerByPin = async (pin) => {
    try {
      // console.log("pin in get", pin);
      const result = await api(`v1/reviewer/pin/${pin}`);
      if (!result.data || result.data.length === 0) {
        return null;
      }
      await updateReviewers(result.data);
      // Return the first reviewer since pin should be unique
      return { ...result.data[0], error: false };
    } catch (error) {
      console.error("Error fetching review by pin:", error);
      return { error: error };
    }
  };

  const getReviewerByPinAndEmail = async (pin, email) => {
    try {
      // console.log("pin in get", pin);
      const result = await api(`v1/reviewer/pin/${pin}`);
      if (!result.data || result.data.length === 0) {
        return null;
      }

      //filter out the reviewer by email
      const reviewer = result.data.filter((el) => el.email === email);

      if (reviewer.length === 0) {
        return null;
      }

      await updateReviewers(reviewer);

      // await updateReviewers(result.data);
      // return { reviewer: result.data, error: false };
      return { error: false };
    } catch (error) {
      console.error("Error fetching review by pin:", error);
      return { error: error };
    }
  };

  const saveElementsAndReturnPins = async (elements) => {
    //extract the pin from the elements and return as list. exclude nulls.
    // console.log("elements", elements);
    const pins = elements.map((el) => el?.pin).filter((el) => el);
    // console.log("pins", pins);
    return pins;
  };

  const archiveReviewer = (reviewer) => {
    const hash = reviewer.emailHash.split("/").pop();
    api(`v1/reviewer/archive/${hash}`, {
      method: "DELETE",
    })
      .then((res) => {
        updateReviewers(res.data.reviewers);
      })
      .catch((err) => {
        // console.error("Error archiving reviewer:", err);
      });
  };

  const createDummy = () => {
    api(`v1/reviewer/createdummy`, {
      method: "POST",
    })
      .then((res) => {
        // console.log("res.data", res.data);
        updateReviewers(res.data.reviewers);
      })
      .catch((err) => {
        console.error("Error creating dummy reviewer:", err);
      });
  };
  const fetchYesChoiceReviewers = async () => {
    loading.value = true;
    try {
      const res = await api("v1/reviewer/yes-choice");
      if (!res) {
        console.error("Error fetching yes-choice reviewers:", res);
        return;
      }
      totalReviewerCount.value = res.data.totalReviewerCount;
      updateReviewers(res.data.reviewers);
    } catch (error) {
      console.error("Error fetching yes-choice reviewers:", error);
    } finally {
      setTimeout(() => {
        loading.value = false;
      }, 1000);
    }
  };

  return {
    reviewers,
    fetchReviewers,
    createReviewer,
    archiveReviewer,
    loading,
    imageDataURIs,
    totalReviewerCount,
    createDummy,
    getReviewerPins,
    getReviewerByPin,
    getReviewerByPinAndEmail,
    fetchYesChoiceReviewers,
  };
};
